import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import XLSX from "xlsx-js-style";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import projectPerformanceSvg from "assets/folders/report.svg";
import { selectCurrentUser } from "store/features/auth/authSelectors";
import { getSecureLocalItem } from "utils/secureSessionStorage";

import {
  Box,
  Grid,
  Paper,
  Typography,
  useTheme,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Stack,
  TableRow,
  CircularProgress,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Collapse,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Visibility as VisibilityIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import { fetchProjects } from "store/features/company/projectManagement/project/projectActions";
import {
  fetchEmployees,
  fetchEmployeeWorkloads,
} from "store/features/company/employee/employeeActions";
import { fetchDepartments } from "store/features/company/department/departmentActions";
import { fetchProjectPerformanceReport } from "store/features/company/projectManagement/report/reportActions";

import { selectProjects } from "store/features/company/projectManagement/project/projectSelectors";
import {
  selectEmployees,
  selectEmployeeWorkloads,
} from "store/features/company/employee/employeeSelectors";
import { selectDepartments } from "store/features/company/department/departmentSelectors";
import {
  selectReportData,
  selectReportLoading,
  selectReportError,
} from "store/features/company/projectManagement/report/reportSelectors";

const getReviewFileInfo = (url) => {
  if (!url || typeof url !== "string")
    return { type: "unknown", name: "File", isImage: false, isPdf: false };
  const cleanUrl = url.split("?")[0];
  const name =
    cleanUrl.substring(cleanUrl.lastIndexOf("/") + 1) || "Deliverable";
  const ext = name.split(".").pop().toLowerCase();
  const isImage = ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext);
  const isPdf = ext === "pdf";
  return { type: ext, name, isImage, isPdf };
};

const TaskRow = ({ task, projectName, getStatusColor, getPriorityColor }) => {
  const [open, setOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const theme = useTheme();

  return (
    <React.Fragment>
      <TableRow hover sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell
          padding="checkbox"
          sx={{ border: "none", boxShadow: "none" }}
        >
          {task.reviews?.length > 0 && (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>
            {projectName}
          </Typography>
        </TableCell>
        <TableCell>{task.title}</TableCell>
        <TableCell>{task.assigned_to?.name}</TableCell>
        <TableCell>
          <Chip
            label={task.status?.toUpperCase()?.replace(/_/g, " ")}
            size="small"
            color={getStatusColor(task.status)}
            sx={{ fontSize: "0.7rem" }}
          />
        </TableCell>
        <TableCell>
          <Chip
            label={task.priority?.toUpperCase()}
            size="small"
            color={getPriorityColor(task.priority)}
            sx={{ fontSize: "0.7rem" }}
          />
        </TableCell>
        <TableCell>{task.estimated_hours} hrs</TableCell>
        <TableCell>
          <Typography variant="caption" display="block">
            <b>Start:</b>{" "}
            {task.start_date
              ? new Date(task.start_date).toLocaleDateString()
              : "N/A"}
          </Typography>
          <br />
          <Typography variant="caption" display="block">
            <b>Due:</b>{" "}
            {task.due_date
              ? new Date(task.due_date).toLocaleDateString()
              : "N/A"}
          </Typography>
          <br />
          <Typography variant="caption" display="block">
            <b>Status:</b>{" "}
            {(() => {
              const today = new Date();
              const due = task.due_date ? new Date(task.due_date) : null;

              if (task.completed_at) {
                const completed = new Date(task.completed_at);

                if (!due) return "Completed";

                const diff = Math.floor(
                  (completed - due) / (1000 * 60 * 60 * 24),
                );

                if (diff < 0)
                  return `Completed ${Math.abs(diff)} day${
                    Math.abs(diff) > 1 ? "s" : ""
                  } early`;
                if (diff === 0) return "Completed on time";
                return `Completed ${diff} day${diff > 1 ? "s" : ""} late`;
              }

              if (!due) return "No due date";

              const remaining = Math.floor(
                (due - today) / (1000 * 60 * 60 * 24),
              );

              if (remaining > 0)
                return `${remaining} day${remaining > 1 ? "s" : ""} remaining`;
              if (remaining === 0) return "Due today";
              return `Overdue by ${Math.abs(remaining)} day${
                Math.abs(remaining) > 1 ? "s" : ""
              }`;
            })()}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                margin: 2,
                p: 2,
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "#f8f9fa",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                component="div"
                sx={{ fontSize: "0.9rem", fontWeight: 600 }}
              >
                Task Reviews
              </Typography>
              {task.reviews && task.reviews.length > 0 ? (
                <Table size="small" aria-label="reviews">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Reviewer</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Purpose</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Review Note
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Deliverable / File
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Submitted At
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {task.reviews.map((reviewRow) => {
                      const fileUrl = reviewRow.deliverables || reviewRow.file;
                      const fileInfo = fileUrl
                        ? getReviewFileInfo(fileUrl)
                        : null;
                      return (
                        <TableRow key={reviewRow.id}>
                          <TableCell sx={{ border: "none", boxShadow: "none" }}>
                            {reviewRow.reviewer}
                          </TableCell>
                          <TableCell sx={{ border: "none", boxShadow: "none" }}>
                            <Chip
                              label={(
                                reviewRow.purpose || "General"
                              ).toUpperCase()}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ fontSize: "0.65rem", fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell sx={{ border: "none", boxShadow: "none" }}>
                            {reviewRow.review}
                          </TableCell>
                          <TableCell sx={{ border: "none", boxShadow: "none" }}>
                            {fileUrl ? (
                              <Tooltip title="Preview Deliverable / File">
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={
                                    fileInfo?.isImage ? (
                                      <ImageIcon sx={{ fontSize: "0.9rem" }} />
                                    ) : fileInfo?.isPdf ? (
                                      <PdfIcon sx={{ fontSize: "0.9rem" }} />
                                    ) : (
                                      <AttachFileIcon
                                        sx={{ fontSize: "0.9rem" }}
                                      />
                                    )
                                  }
                                  onClick={() =>
                                    setPreviewFile({
                                      url: fileUrl,
                                      name: fileInfo?.name || "Deliverable",
                                      isImage: fileInfo?.isImage,
                                      isPdf: fileInfo?.isPdf,
                                      purpose: reviewRow.purpose || "General",
                                      reviewer: reviewRow.reviewer,
                                      review: reviewRow.review,
                                    })
                                  }
                                  sx={{
                                    fontSize: "0.72rem",
                                    py: 0.3,
                                    px: 1.2,
                                    textTransform: "none",
                                    borderRadius: "8px",
                                    borderColor:
                                      theme.palette.mode === "dark"
                                        ? "rgba(255,255,255,0.2)"
                                        : "rgba(0,0,0,0.23)",
                                  }}
                                >
                                  Preview File
                                </Button>
                              </Tooltip>
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ border: "none", boxShadow: "none" }}>
                            {reviewRow.time
                              ? new Date(reviewRow.time).toLocaleString()
                              : "N/A"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No reviews for this task yet.
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Review File Preview Dialog */}
      <Dialog
        open={Boolean(previewFile)}
        onClose={() => setPreviewFile(null)}
        maxWidth={previewFile?.isImage || previewFile?.isPdf ? "md" : "sm"}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              minWidth: 0,
              flex: 1,
              mr: 1,
            }}
          >
            {previewFile?.isImage ? (
              <ImageIcon color="primary" />
            ) : previewFile?.isPdf ? (
              <PdfIcon color="error" />
            ) : (
              <AttachFileIcon color="action" />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  noWrap
                  sx={{ maxWidth: 350 }}
                >
                  {previewFile?.name || "Deliverable"}
                </Typography>
                {previewFile?.purpose && (
                  <Chip
                    label={previewFile.purpose.toUpperCase()}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: "0.62rem", height: 20 }}
                  />
                )}
              </Box>
              {previewFile?.reviewer && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  By {previewFile.reviewer}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setPreviewFile(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            p: 2.5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 320,
            bgcolor:
              theme.palette.mode === "dark" ? "rgba(0,0,0,0.2)" : "#fafafa",
          }}
        >
          {previewFile?.isImage ? (
            <Box
              component="img"
              src={previewFile.url}
              alt={previewFile.name}
              sx={{
                maxWidth: "100%",
                maxHeight: "65vh",
                objectFit: "contain",
                borderRadius: "8px",
                boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
              }}
            />
          ) : previewFile?.isPdf ? (
            <Box
              component="iframe"
              src={previewFile.url}
              title="PDF Preview"
              sx={{
                width: "100%",
                height: "65vh",
                border: "none",
                borderRadius: "8px",
              }}
            />
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <DescriptionIcon
                sx={{ fontSize: 60, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="body1" fontWeight={600} gutterBottom>
                {previewFile?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Direct in-browser preview is not available for this file type.
              </Typography>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                href={previewFile?.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open / Download File
              </Button>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: 1,
            borderColor: "divider",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ maxWidth: 300 }}
            noWrap
          >
            {previewFile?.review ? `Note: "${previewFile.review}"` : ""}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              startIcon={<OpenInNewIcon />}
              size="small"
              onClick={() => window.open(previewFile?.url, "_blank")}
            >
              Open in New Tab
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              href={previewFile?.url}
              download
              target="_blank"
              rel="noopener noreferrer"
            >
              Download
            </Button>
            <Button size="small" onClick={() => setPreviewFile(null)}>
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

const ProjectReport = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const projects = useSelector(selectProjects);
  const employeesData = useSelector(selectEmployees);
  const workloads = useSelector(selectEmployeeWorkloads) || [];
  const departmentsData = useSelector(selectDepartments);

  const reportData = useSelector(selectReportData);
  const loading = useSelector(selectReportLoading);
  const error = useSelector(selectReportError);

  const employees = employeesData?.employees || employeesData || [];
  const departments = departmentsData?.departments || departmentsData || [];

  // User & Role Context
  const currentUser = useSelector(selectCurrentUser);
  const userRole =
    getSecureLocalItem("role") ||
    currentUser?.role ||
    currentUser?.role_name ||
    "Company";
  const isCompanyOrAdmin =
    userRole?.toLowerCase() === "company" ||
    userRole?.toLowerCase() === "admin" ||
    userRole?.toLowerCase() === "super_admin";

  const rawUser = getSecureLocalItem("user");
  const storedUser = rawUser
    ? typeof rawUser === "string"
      ? JSON.parse(rawUser)
      : rawUser
    : null;
  const user = storedUser || currentUser;
  const userId = user?.id || user?.user_id;
  const userEmail = user?.email;

  // Find the logged-in employee record
  const loggedInEmp = useMemo(() => {
    if (!employees || employees.length === 0) return null;
    return (
      employees.find(
        (e) =>
          (userId && (e.id === userId || e.user_id === userId)) ||
          (userEmail && e.email && e.email.toLowerCase() === userEmail.toLowerCase())
      ) || null
    );
  }, [employees, userId, userEmail]);

  // Compute reporting hierarchy (self + employees reporting to him directly/indirectly)
  const { hierarchyEmployeeIds, hierarchyEmployees } = useMemo(() => {
    if (isCompanyOrAdmin || !loggedInEmp) {
      return {
        hierarchyEmployeeIds: null,
        hierarchyEmployees: employees,
      };
    }

    const managerToReportees = {};
    employees.forEach((emp) => {
      if (emp.reporting_manager_id) {
        if (!managerToReportees[emp.reporting_manager_id]) {
          managerToReportees[emp.reporting_manager_id] = [];
        }
        managerToReportees[emp.reporting_manager_id].push(emp);
      }
    });

    const set = new Set([loggedInEmp.id]);
    const list = [loggedInEmp];
    const queue = [...(managerToReportees[loggedInEmp.id] || [])];

    while (queue.length > 0) {
      const child = queue.shift();
      if (!set.has(child.id)) {
        set.add(child.id);
        list.push(child);
        const next = managerToReportees[child.id] || [];
        queue.push(...next);
      }
    }

    return {
      hierarchyEmployeeIds: set,
      hierarchyEmployees: list,
    };
  }, [isCompanyOrAdmin, loggedInEmp, employees]);

  // Filter project managers based on the role
  const projectManagers = workloads.filter(
    (w) => w.role && w.role.toLowerCase() === "project_manager",
  );

  // Available Filter Options scoped to hierarchy
  const availableEmployees = useMemo(() => {
    if (isCompanyOrAdmin || !hierarchyEmployeeIds) return employees;
    return hierarchyEmployees;
  }, [isCompanyOrAdmin, hierarchyEmployeeIds, employees, hierarchyEmployees]);

  const availableProjects = useMemo(() => {
    if (isCompanyOrAdmin || !hierarchyEmployeeIds) return projects || [];
    return (projects || []).filter((proj) => {
      // 1. Is PM in hierarchy?
      if (
        proj.project_manager_id &&
        hierarchyEmployeeIds.has(Number(proj.project_manager_id))
      ) {
        return true;
      }
      // 2. Is any team member in hierarchy?
      let members = proj.team_members;
      if (typeof members === "string") {
        try {
          members = JSON.parse(members);
        } catch {
          members = [];
        }
      }
      if (Array.isArray(members)) {
        if (
          members.some((m) => {
            const id = typeof m === "object" && m !== null ? (m.id || m.employee_id) : m;
            return hierarchyEmployeeIds.has(Number(id));
          })
        ) {
          return true;
        }
      }
      return false;
    });
  }, [isCompanyOrAdmin, hierarchyEmployeeIds, projects]);

  const availableProjectManagers = useMemo(() => {
    if (isCompanyOrAdmin || !hierarchyEmployeeIds) return projectManagers;
    return projectManagers.filter((pm) =>
      hierarchyEmployeeIds.has(Number(pm.employee_id || pm.id))
    );
  }, [isCompanyOrAdmin, hierarchyEmployeeIds, projectManagers]);

  const availableDepartments = useMemo(() => {
    if (isCompanyOrAdmin || !hierarchyEmployeeIds) return departments;
    const deptIds = new Set();
    hierarchyEmployees.forEach((emp) => {
      if (emp.department_id) deptIds.add(Number(emp.department_id));
    });
    availableProjects.forEach((proj) => {
      if (proj.department_id) deptIds.add(Number(proj.department_id));
    });
    return departments.filter((d) => deptIds.has(Number(d.id)));
  }, [isCompanyOrAdmin, hierarchyEmployeeIds, departments, hierarchyEmployees, availableProjects]);

  const [filters, setFilters] = useState({
    from_date: "",
    to_date: "",
    project_ids: [],
    department_ids: [],
    project_manager_ids: [],
    employee_ids: [],
    status: [],
    priority: [],
  });

  const [groupBy, setGroupBy] = useState("project");

  useEffect(() => {
    dispatch(fetchProjects());
    dispatch(fetchEmployees());
    dispatch(fetchEmployeeWorkloads());
    dispatch(fetchDepartments());
  }, [dispatch]);

  const handleMultipleSelectChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: typeof value === "string" ? value.split(",") : value,
    });
  };

  const handleInputChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleDateChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]:
        value && dayjs(value).isValid()
          ? dayjs(value).format("YYYY-MM-DD")
          : "",
    }));
  };

  const generateReport = () => {
    const payloadFilters = { ...filters };
    // For employee, default to his hierarchy if no specific employee was selected
    if (!isCompanyOrAdmin && hierarchyEmployeeIds) {
      if (!payloadFilters.employee_ids || payloadFilters.employee_ids.length === 0) {
        payloadFilters.employee_ids = Array.from(hierarchyEmployeeIds);
      }
    }
    const payload = {
      filters: payloadFilters,
      group_by: groupBy,
      include: ["summary", "projects", "tasks", "reviews"],
    };
    dispatch(fetchProjectPerformanceReport(payload));
  };

  const downloadPDF = async () => {
    const input = document.getElementById("report-content");
    if (!input) return;

    try {
      const canvas = await html2canvas(input, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = pdfHeight;
      let position = 0;
      const pageHeight = pdf.internal.pageSize.getHeight();

      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
        heightLeft -= pageHeight;
      }

      pdf.save("project_performance_report.pdf");
    } catch (error) {
      console.error("Error generating PDF", error);
    }
  };

  const downloadReport = () => {
    if (!reportData) return;

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    const yellowHeaderStyle = {
      font: {
        bold: true,
        sz: 14,
        color: { rgb: "000000" },
      },
      fill: {
        fgColor: { rgb: "FFF200" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
    };

    let row = 0;

    // ===================================================
    // PROJECT PERFORMANCE REPORT
    // ===================================================

    XLSX.utils.sheet_add_aoa(
      ws,
      [
        [
          `PROJECT PERFORMANCE REPORT (${
            filters.from_date
              ? new Date(filters.from_date).toLocaleDateString()
              : "N/A"
          } – ${
            filters.to_date
              ? new Date(filters.to_date).toLocaleDateString()
              : "N/A"
          })`,
        ],
      ],
      { origin: `A${row + 1}` },
    );

    ws["!merges"] = [{ s: { r: row, c: 0 }, e: { r: row, c: 10 } }];

    ws[`A${row + 1}`].s = yellowHeaderStyle;

    row += 2;

    // ===================================================
    // SUMMARY
    // ===================================================

    XLSX.utils.sheet_add_aoa(ws, [["SUMMARY"]], { origin: `A${row + 1}` });

    ws["!merges"].push({
      s: { r: row, c: 0 },
      e: { r: row, c: 1 },
    });

    ws[`A${row + 1}`].s = yellowHeaderStyle;

    row++;

    XLSX.utils.sheet_add_aoa(
      ws,
      [
        ["Metric", "Value"],
        ["Total Projects", reportData.summary.total_projects],
        ["Planning Projects", reportData.summary.planning_projects],
        ["Active Projects", reportData.summary.active_projects],
        ["Completed Projects", reportData.summary.completed_projects],
        ["On Hold", reportData.summary.on_hold_projects],
        ["Cancelled", reportData.summary.cancelled_projects],
        ["Total Tasks", reportData.summary.total_tasks],
        ["Todo", reportData.summary.todo_tasks],
        ["In Progress", reportData.summary.in_progress_tasks],
        ["Review", reportData.summary.review_tasks],
        ["Blocked", reportData.summary.blocked_tasks],
        ["Paused", reportData.summary.paused_tasks],
        ["Completed Tasks", reportData.summary.completed_tasks],
        ["Estimated Hours", reportData.summary.estimated_hours],
        ["Actual Hours", reportData.summary.actual_hours],
        ["Completion %", reportData.summary.completion_percentage + "%"],
      ],
      { origin: `A${row + 1}` },
    );

    row += 18;

    // ===================================================
    // PROJECTS
    // ===================================================

    XLSX.utils.sheet_add_aoa(ws, [["PROJECTS"]], { origin: `A${row + 1}` });

    ws["!merges"].push({
      s: { r: row, c: 0 },
      e: { r: row, c: 8 },
    });

    ws[`A${row + 1}`].s = yellowHeaderStyle;

    row++;

    XLSX.utils.sheet_add_aoa(
      ws,
      [
        [
          "Project",
          "Manager",
          "Department",
          "Status",
          "Priority",
          "Budget",
          "Team Size",
          "Tasks",
          "Completion %",
        ],
      ],
      { origin: `A${row + 1}` },
    );

    row++;

    reportData.projects.forEach((item) => {
      XLSX.utils.sheet_add_aoa(
        ws,
        [
          [
            item.project.name,
            item.project.manager?.name || "",
            item.project.department?.name || "",
            item.project.status,
            item.project.priority,
            item.project.budget,
            item.project.team_size,
            item.statistics.total_tasks,
            item.statistics.completion_percentage + "%",
          ],
        ],
        { origin: `A${row + 1}` },
      );

      row++;
    });

    row += 2;

    // ===================================================
    // TASK DETAILS
    // ===================================================

    XLSX.utils.sheet_add_aoa(ws, [["TASK DETAILS"]], { origin: `A${row + 1}` });

    ws["!merges"].push({
      s: { r: row, c: 0 },
      e: { r: row, c: 10 },
    });

    ws[`A${row + 1}`].s = yellowHeaderStyle;

    row++;

    XLSX.utils.sheet_add_aoa(
      ws,
      [
        [
          "Project",
          "Task",
          "Assigned To",
          "Created By",
          "Status",
          "Priority",
          "Estimated",
          "Actual",
          "Start",
          "Due",
          "Reviews",
        ],
      ],
      { origin: `A${row + 1}` },
    );

    row++;

    reportData.projects.forEach((project) => {
      project.tasks.forEach((task) => {
        XLSX.utils.sheet_add_aoa(
          ws,
          [
            [
              project.project.name,
              task.title,
              task.assigned_to?.name || "",
              task.created_by?.name || "",
              task.status,
              task.priority,
              task.estimated_hours,
              task.actual_hours,
              task.start_date
                ? new Date(task.start_date).toLocaleDateString()
                : "",
              task.due_date ? new Date(task.due_date).toLocaleDateString() : "",
              task.reviews?.length
                ? task.reviews
                    .map(
                      (r) =>
                        `${r.reviewer} (${r.purpose || "General"})${
                          r.deliverables || r.file
                            ? ` [File: ${r.deliverables || r.file}]`
                            : ""
                        }${
                          r.time
                            ? ` [${new Date(r.time).toLocaleString()}]`
                            : ""
                        }: ${r.review}`,
                    )
                    .join(" | ")
                : "",
            ],
          ],
          { origin: `A${row + 1}` },
        );

        row++;
      });
    });

    // Column widths
    ws["!cols"] = [
      { wch: 25 },
      { wch: 45 },
      { wch: 20 },
      { wch: 20 },
      { wch: 15 },
      { wch: 15 },
      { wch: 12 },
      { wch: 12 },
      { wch: 15 },
      { wch: 15 },
      { wch: 60 },
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Project Report");

    XLSX.writeFile(wb, "Project_Report.xlsx");
  };
  // UI Helpers
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
      case "in_progress":
        return "primary";
      case "completed":
      case "done":
        return "success";
      case "planning":
      case "todo":
        return "info";
      case "on_hold":
      case "paused":
      case "blocked":
        return "warning";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "urgent":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "low":
        return "success";
      default:
        return "default";
    }
  };

  const renderSummaryTable = (summary) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Overall Summary
      </Typography>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.divider}` }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="summary table">
          <TableHead
            sx={{
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.05)"
                  : "#f9fafb",
            }}
          >
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Total Projects</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Active Projects</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Total Tasks</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Todo</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>In Progress</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Pending</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Est. Hours</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{summary.total_projects}</TableCell>
              <TableCell>{summary.active_projects}</TableCell>
              <TableCell>{summary.total_tasks}</TableCell>
              <TableCell>{summary.todo_tasks}</TableCell>
              <TableCell>{summary.in_progress_tasks}</TableCell>
              <TableCell>{summary.pending_tasks}</TableCell>
              <TableCell>{summary.estimated_hours} hrs</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderProjectsTable = (projectsList) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Projects Overview
      </Typography>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.divider}` }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="projects table">
          <TableHead
            sx={{
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.05)"
                  : "#f9fafb",
            }}
          >
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>
                Project Name (Code)
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Manager</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Budget</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Timeline</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Est. Hours</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>
                Team Size
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projectsList.map((p, idx) => (
              <TableRow
                key={idx}
                hover
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {p.project?.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.project?.code}
                  </Typography>
                </TableCell>
                <TableCell>{p.project?.manager?.name}</TableCell>
                <TableCell>{p.project?.department?.name}</TableCell>
                <TableCell>
                  <Chip
                    label={p.project?.status?.toUpperCase()}
                    size="small"
                    color={getStatusColor(p.project?.status)}
                    sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                  />
                </TableCell>
                <TableCell>₹{p.project?.budget?.toLocaleString()}</TableCell>
                <TableCell>
                  <Typography variant="caption" display="block">
                    Start:{" "}
                    {p.project?.start_date
                      ? new Date(p.project.start_date).toLocaleDateString()
                      : "N/A"}
                  </Typography>
                  <Typography variant="caption" display="block">
                    End:{" "}
                    {p.project?.end_date
                      ? new Date(p.project.end_date).toLocaleDateString()
                      : "N/A"}
                  </Typography>
                </TableCell>
                <TableCell>{p.statistics?.estimated_hours} hrs</TableCell>
                <TableCell align="center">{p.project?.team_size}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderTasksTable = (projectsList) => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Task Breakdown
      </Typography>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.divider}` }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="tasks table">
          <TableHead
            sx={{
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.05)"
                  : "#f9fafb",
            }}
          >
            <TableRow>
              <TableCell />
              <TableCell sx={{ fontWeight: 600 }}>Project</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Task Title</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Assigned To</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Est. Hours</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Timeline</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projectsList.map((p) => {
              return p.tasks?.map((task) => (
                <TaskRow
                  key={`${p.project?.id}-${task.id}`}
                  task={task}
                  projectName={p.project?.name}
                  getStatusColor={getStatusColor}
                  getPriorityColor={getPriorityColor}
                />
              ));
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" spacing={2.5} alignItems="center" sx={{ mb: 3 }}>
        <Box
          component="img"
          src={projectPerformanceSvg}
          alt="Project Performance Logo"
          onError={(e) => {
            e.target.style.display = "none";
          }}
          sx={{
            width: { xs: 56, lg: 64 },
            height: { xs: 56, lg: 64 },
            objectFit: "contain",
          }}
        />
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Project Performance Report
        </Typography>
      </Stack>
      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor:
            theme.palette.mode === "dark" ? "background.paper" : "#ffffff",
        }}
        elevation={0}
      >
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Report Filters
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="From Date"
                value={filters.from_date ? dayjs(filters.from_date) : null}
                onChange={(newValue) => handleDateChange("from_date", newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="To Date"
                value={filters.to_date ? dayjs(filters.to_date) : null}
                onChange={(newValue) => handleDateChange("to_date", newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Projects</InputLabel>
                <Select
                  multiple
                  name="project_ids"
                  value={filters.project_ids}
                  onChange={handleMultipleSelectChange}
                  sx={{ minWidth: "150px" }}
                  input={<OutlinedInput label="Projects" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => {
                        const project = projects.find(
                          (p) => String(p.id) === String(value),
                        );
                        return (
                          <Chip
                            key={value}
                            label={project ? project.name : value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {availableProjects?.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      <Checkbox
                        checked={filters.project_ids.some(
                          (id) => String(id) === String(p.id),
                        )}
                      />
                      <ListItemText primary={p.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Departments</InputLabel>
                <Select
                  multiple
                  name="department_ids"
                  value={filters.department_ids}
                  onChange={handleMultipleSelectChange}
                  sx={{ minWidth: "150px" }}
                  input={<OutlinedInput label="Departments" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => {
                        const dept = departments.find(
                          (d) => String(d.id) === String(value),
                        );
                        return (
                          <Chip
                            key={value}
                            label={dept ? dept.name : value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {availableDepartments?.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      <Checkbox
                        checked={filters.department_ids.some(
                          (id) => String(id) === String(d.id),
                        )}
                      />
                      <ListItemText primary={d.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Project Managers</InputLabel>
                <Select
                  multiple
                  name="project_manager_ids"
                  value={filters.project_manager_ids}
                  onChange={handleMultipleSelectChange}
                  sx={{ minWidth: "150px" }}
                  input={<OutlinedInput label="Project Managers" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => {
                        const pm = projectManagers.find(
                          (pm) => String(pm.employee_id) === String(value),
                        );
                        return (
                          <Chip
                            key={value}
                            label={pm ? pm.name : value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {availableProjectManagers?.map((pm) => (
                    <MenuItem key={pm.employee_id} value={pm.employee_id}>
                      <Checkbox
                        checked={filters.project_manager_ids.some(
                          (id) => String(id) === String(pm.employee_id),
                        )}
                      />
                      <ListItemText primary={pm.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                size="small"
                options={availableEmployees || []}
                getOptionLabel={(e) => `${e.first_name} ${e.last_name}`}
                value={(filters.employee_ids || [])
                  .map((id) =>
                    employees.find((e) => String(e.id) === String(id)),
                  )
                  .filter(Boolean)}
                onChange={(event, newValue) =>
                  setFilters((prev) => ({
                    ...prev,
                    employee_ids: newValue.map((e) => e.id),
                  }))
                }
                isOptionEqualToValue={(option, value) =>
                  String(option.id) === String(value.id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Employees"
                    placeholder="Type to search…"
                  />
                )}
                sx={{
                  "& .MuiAutocomplete-input": {
                    minWidth: "120px !important",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  name="status"
                  value={filters.status}
                  sx={{ minWidth: "150px" }}
                  onChange={handleMultipleSelectChange}
                  input={<OutlinedInput label="Status" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={value.replace(/_/g, " ")}
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                >
                  {[
                    "active",
                    "completed",
                    "on_hold",
                    "planning",
                    "cancelled",
                  ].map((st) => (
                    <MenuItem key={st} value={st}>
                      <Checkbox checked={filters.status.indexOf(st) > -1} />
                      <ListItemText
                        primary={st.replace(/_/g, " ")}
                        sx={{ textTransform: "capitalize" }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  multiple
                  name="priority"
                  value={filters.priority}
                  onChange={handleMultipleSelectChange}
                  sx={{ minWidth: "150px" }}
                  input={<OutlinedInput label="Priority" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {["urgent", "high", "medium", "low"].map((pr) => (
                    <MenuItem key={pr} value={pr}>
                      <Checkbox checked={filters.priority.indexOf(pr) > -1} />
                      <ListItemText
                        primary={pr}
                        sx={{ textTransform: "capitalize" }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Group By</InputLabel>
                <Select
                  name="group_by"
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  label="Group By"
                >
                  <MenuItem value="project">Project</MenuItem>
                  <MenuItem value="department">Department</MenuItem>
                  <MenuItem value="employee">Employee</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={generateReport}
                disabled={loading}
                sx={{ minWidth: 160, py: 1 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Generate Report"
                )}
              </Button>
              {error && (
                <Typography color="error" variant="body2" sx={{ ml: 2 }}>
                  {error}
                </Typography>
              )}
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Paper>

      {reportData && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h5" fontWeight={600}>
              Report Results
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button variant="outlined" color="primary" onClick={downloadPDF}>
                Download PDF
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => downloadReport("xlsx")}
              >
                Download Excel
              </Button>
            </Box>
          </Box>
          <Box
            id="report-content"
            sx={{
              p: 2,
              bgcolor:
                theme.palette.mode === "dark" ? "background.paper" : "#ffffff",
              borderRadius: 2,
            }}
          >
            {reportData.summary && renderSummaryTable(reportData.summary)}
            {reportData.projects && reportData.projects.length > 0 ? (
              <>
                {renderProjectsTable(reportData.projects)}
                {renderTasksTable(reportData.projects)}
              </>
            ) : (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="body1" color="text.secondary">
                  No projects or tasks found matching the selected filters.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ProjectReport;
